import { Flex, Spin } from 'antd';
import { useEffect, type ReactNode } from 'react';
import { Navigate, Route, BrowserRouter as Router, Routes } from 'react-router-dom';
import { LoginPage } from '../features/auth/LoginPage';
import { useSession } from '../features/auth/session';
import { CustomerDetailPage } from '../features/customers/CustomerDetailPage';
import { CustomerListPage } from '../features/customers/CustomerListPage';
import { LeadDetailPage } from '../features/crm/LeadDetailPage';
import { LeadListPage } from '../features/crm/LeadListPage';
import { BookingDetailPage } from '../features/sales/BookingDetailPage';
import { FinancingListPage } from '../features/sales/FinancingListPage';
import { DeliveryListPage } from '../features/vehicles/DeliveryListPage';
import { DeliveryPage } from '../features/vehicles/DeliveryPage';
import { RegistrationCasePage } from '../features/vehicles/RegistrationCasePage';
import { RegistrationDashboard } from '../features/vehicles/RegistrationDashboard';
import { ScanPage } from '../features/vehicles/ScanPage';
import { UnitListPage } from '../features/vehicles/UnitListPage';
import { UnitPage } from '../features/vehicles/UnitPage';
import { CounterSalePage } from '../features/parts/CounterSalePage';
import { StockCountPage } from '../features/parts/StockCountPage';
import { StockCountsPage } from '../features/parts/StockCountsPage';
import { WarehousePage } from '../features/parts/WarehousePage';
import { InvoicePage } from '../features/pos/InvoicePage';
import { TillPage } from '../features/pos/TillPage';
import { CounterPage } from '../features/workshop/CounterPage';
import { JobBoardPage } from '../features/workshop/JobBoardPage';
import { JobCardPage } from '../features/workshop/JobCardPage';
import { TechnicianPage } from '../features/workshop/TechnicianPage';
import { BookingListPage } from '../features/sales/BookingListPage';
import { QuotationDetailPage } from '../features/sales/QuotationDetailPage';
import { QuotationListPage } from '../features/sales/QuotationListPage';
import { CasePage } from '../features/service-desk/CasePage';
import { CasesPage } from '../features/service-desk/CasesPage';
import { FollowUpsPage } from '../features/service-desk/FollowUpsPage';
import { RemindersPage } from '../features/service-desk/RemindersPage';
import { RetentionPage } from '../features/service-desk/RetentionPage';
import { ServiceDeskSettingsPage } from '../features/service-desk/ServiceDeskSettingsPage';
import { AppShell } from './layout/AppShell';
import { ModelMapPage } from '../features/vehicles/ModelMapPage';
import { UsersPage } from '../features/iam/UsersPage';
import { CompanyPage } from '../features/iam/CompanyPage';
import { RolesPage } from '../features/iam/RolesPage';
import { ChangePasswordPage } from '../features/iam/ChangePasswordPage';
import { AccountsPage } from '../features/accounting/AccountsPage';
import { InsightsPage } from '../features/analytics/InsightsPage';

export function AppRoutes(): ReactNode {
  const status = useSession((s) => s.status);
  const mustChangePassword = useSession((s) => s.user?.mustChangePassword === true);
  const restore = useSession((s) => s.restore);

  useEffect(() => {
    // One probe at startup: a reload has no access token but may still hold the
    // refresh cookie, so ask the API who we are rather than bouncing to login.
    void restore();
  }, [restore]);

  if (status === 'unknown') {
    return (
      <Flex align="center" justify="center" style={{ minHeight: '100dvh' }}>
        <Spin size="large" />
      </Flex>
    );
  }

  return (
    <Router>
      <Routes>
        {status === 'signed-out' ? (
          <>
            <Route path="/login" element={<LoginPage />} />
            <Route path="*" element={<Navigate to="/login" replace />} />
          </>
        ) : mustChangePassword ? (
          // Outside the shell: its menus and counters call routes the API
          // refuses until the password is changed (spec 2026-10-27).
          <Route path="*" element={<ChangePasswordPage firstSignIn />} />
        ) : (
          <Route element={<AppShell />}>
            <Route path="/leads" element={<LeadListPage />} />
            <Route path="/leads/:uid" element={<LeadDetailPage />} />
            <Route path="/quotations" element={<QuotationListPage />} />
            <Route path="/quotations/:uid" element={<QuotationDetailPage />} />
            <Route path="/bookings" element={<BookingListPage />} />
            <Route path="/bookings/:uid" element={<BookingDetailPage />} />
            <Route path="/financing" element={<FinancingListPage />} />
            <Route path="/model-map" element={<ModelMapPage />} />
            <Route path="/accounts" element={<AccountsPage />} />
            <Route path="/insights" element={<InsightsPage />} />
            <Route path="/users" element={<UsersPage />} />
            <Route path="/roles" element={<RolesPage />} />
            <Route path="/company" element={<CompanyPage />} />
            <Route path="/change-password" element={<ChangePasswordPage />} />
            <Route path="/units" element={<UnitListPage />} />
            <Route path="/units/:uid" element={<UnitPage />} />
            <Route path="/scan" element={<ScanPage />} />
            <Route path="/registration" element={<RegistrationDashboard />} />
            <Route path="/registration/:uid" element={<RegistrationCasePage />} />
            <Route path="/deliveries" element={<DeliveryListPage />} />
            <Route path="/deliveries/:uid" element={<DeliveryPage />} />
            <Route path="/workshop" element={<CounterPage />} />
            <Route path="/workshop/board" element={<JobBoardPage />} />
            <Route path="/workshop/bench" element={<TechnicianPage />} />
            <Route path="/workshop/job-cards/:uid" element={<JobCardPage />} />
            <Route path="/warehouse" element={<WarehousePage />} />
            <Route path="/parts/counts" element={<StockCountsPage />} />
            <Route path="/parts/counts/:uid" element={<StockCountPage />} />
            <Route path="/parts/counter" element={<CounterSalePage />} />
            <Route path="/pos" element={<TillPage />} />
            <Route path="/pos/invoices/:uid" element={<InvoicePage />} />
            <Route path="/cases" element={<CasesPage />} />
            <Route path="/cases/:uid" element={<CasePage />} />
            <Route path="/follow-ups" element={<FollowUpsPage />} />
            <Route path="/reminders" element={<RemindersPage />} />
            <Route path="/retention" element={<RetentionPage />} />
            <Route path="/service-settings" element={<ServiceDeskSettingsPage />} />
            <Route path="/customers" element={<CustomerListPage />} />
            <Route path="/customers/:uid" element={<CustomerDetailPage />} />
            <Route path="*" element={<Navigate to="/leads" replace />} />
          </Route>
        )}
      </Routes>
    </Router>
  );
}
